/**
 * Add Class Dialog - Form for manually adding a class to the schedule
 *
 * Pre-fills day and start time based on clicked grid cell.
 * Allows selection of class, room, teacher, and duration.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetClassesQuery,
  useGetRoomsQuery,
  useGetTeachersQuery,
  useCreateScheduledClassMutation,
  useCreateClassMutation,
} from '../../../../../api/schedulingApi';
import { calculateEndTime } from '../utils/timeUtils';
import type { ClassFormData } from '../../../../../types/scheduling';

interface AddClassDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  studioId: string;
  prefilledDay: string;
  prefilledStartTime: string;
}

const AddClassDialog: React.FC<AddClassDialogProps> = ({
  open,
  onClose,
  scheduleId,
  studioId,
  prefilledDay,
  prefilledStartTime,
}) => {
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [day, setDay] = useState(prefilledDay);
  const [startTime, setStartTime] = useState(prefilledStartTime);
  const [duration, setDuration] = useState<number>(1); // Default 1 hour

  // Quick-add class dialog state
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [classFormData, setClassFormData] = useState<ClassFormData>({
    class_name: '',
    styles: [],
    level: '',
    age_start: 5,
    age_end: 18,
    duration: 60,
    notes: '',
  });
  const [classFormErrors, setClassFormErrors] = useState<{
    class_name?: string;
    styles?: string;
    level?: string;
    age_start?: string;
    age_end?: string;
    duration?: string;
  }>({});

  // Fetch data
  const { data: classes = [], isLoading: classesLoading } = useGetClassesQuery({
    scheduleId,
    studioId,
  });
  const { data: rooms = [], isLoading: roomsLoading } = useGetRoomsQuery({
    scheduleId,
    studioId,
  });
  const { data: teachers = [], isLoading: teachersLoading } =
    useGetTeachersQuery({ scheduleId, studioId });

  // Create mutations
  const [createScheduledClass, { isLoading: isCreating, error: createError }] =
    useCreateScheduledClassMutation();
  const [createClass, { isLoading: isCreatingClass }] =
    useCreateClassMutation();

  // Update prefilled values when they change
  useEffect(() => {
    setDay(prefilledDay);
    setStartTime(prefilledStartTime);
  }, [prefilledDay, prefilledStartTime]);

  // Update duration when class is selected
  useEffect(() => {
    if (selectedClassId) {
      const selectedClass = classes.find(c => c.id === selectedClassId);
      if (selectedClass) {
        setDuration(selectedClass.duration);
      }
    }
  }, [selectedClassId, classes]);

  const handleClose = () => {
    // Reset form
    setSelectedClassId('');
    setSelectedRoomId('');
    setSelectedTeacherId('');
    setDay(prefilledDay);
    setStartTime(prefilledStartTime);
    setDuration(1);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedClassId || !selectedRoomId) {
      return;
    }

    const endTime = calculateEndTime(startTime, duration);

    try {
      await createScheduledClass({
        scheduleId,
        studioId,
        data: {
          class_obj: selectedClassId,
          room: selectedRoomId,
          teachers: selectedTeacherId ? [selectedTeacherId] : [],
          day,
          start_time: startTime,
          end_time: endTime,
          is_manually_scheduled: true, // Mark as manually-scheduled
        },
      }).unwrap();

      // Success - close dialog and reset
      handleClose();
    } catch (err) {
      // Error handling via createError state
      console.error('Failed to create scheduled class:', err);
    }
  };

  const isLoading = classesLoading || roomsLoading || teachersLoading;
  const canSubmit = selectedClassId && selectedRoomId && !isCreating;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Class to Schedule</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error">
                Failed to add class. Please try again.
              </Alert>
            )}

            {classes.length === 0 && (
              <Alert severity="warning">
                No classes available. Please add classes in the Classes tab
                first.
              </Alert>
            )}

            {rooms.length === 0 && (
              <Alert severity="warning">
                No rooms available. Please add rooms in the Rooms tab first.
              </Alert>
            )}

            <Box>
              <TextField
                select
                label="Class"
                value={selectedClassId}
                onChange={e => setSelectedClassId(e.target.value)}
                required
                disabled={classes.length === 0}
                helperText="Select the class to schedule"
                fullWidth
              >
                {classes.map(cls => {
                  // Display styles as comma-separated list
                  const styleNames = cls.style_details
                    ? cls.style_details.map(s => s.title).join(', ')
                    : cls.styles.join(', ');

                  return (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.class_name} ({styleNames} - {cls.level})
                    </MenuItem>
                  );
                })}
              </TextField>
              <Box
                sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIsQuickAddOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Don&apos;t see the class you need? Add a new class
                </Button>
              </Box>
            </Box>

            <TextField
              select
              label="Room"
              value={selectedRoomId}
              onChange={e => setSelectedRoomId(e.target.value)}
              required
              disabled={rooms.length === 0}
              helperText="Select the room for this class"
            >
              {rooms.map(room => (
                <MenuItem key={room.id} value={room.id}>
                  {room.room_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Teacher (Optional)"
              value={selectedTeacherId}
              onChange={e => setSelectedTeacherId(e.target.value)}
              helperText="Optionally assign a teacher"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {teachers.map(teacher => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.teacher_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Day"
              value={day}
              onChange={e => setDay(e.target.value)}
              required
            >
              {[
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
              ].map(d => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="time"
              label="Start Time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="number"
              label="Duration (hours)"
              value={duration}
              onChange={e => setDuration(parseFloat(e.target.value))}
              required
              inputProps={{ min: 0.25, max: 8, step: 0.25 }}
              helperText="Duration in hours (e.g., 1.5 for 90 minutes)"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {isCreating ? 'Adding...' : 'Add Class'}
        </Button>
      </DialogActions>

      {/* Quick-Add Class Dialog */}
      <Dialog
        open={isQuickAddOpen}
        onClose={() => {
          setIsQuickAddOpen(false);
          setClassFormData({
            class_name: '',
            styles: [],
            level: '',
            age_start: 5,
            age_end: 18,
            duration: 60,
            notes: '',
          });
          setClassFormErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Class</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Class Name"
              value={classFormData.class_name}
              onChange={e =>
                setClassFormData({
                  ...classFormData,
                  class_name: e.target.value,
                })
              }
              error={!!classFormErrors.class_name}
              helperText={classFormErrors.class_name}
              required
            />
            <TextField
              fullWidth
              label="Styles (comma-separated)"
              value={classFormData.styles.join(', ')}
              onChange={e =>
                setClassFormData({
                  ...classFormData,
                  styles: e.target.value
                    .split(',')
                    .map(s => s.trim())
                    .filter(s => s.length > 0),
                })
              }
              error={!!classFormErrors.styles}
              helperText={
                classFormErrors.styles ||
                'Enter one or more styles separated by commas (e.g., "Tap, Ballet")'
              }
              required
            />
            <TextField
              fullWidth
              label="Level"
              value={classFormData.level}
              onChange={e =>
                setClassFormData({ ...classFormData, level: e.target.value })
              }
              error={!!classFormErrors.level}
              helperText={classFormErrors.level}
              required
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Age Start"
                value={classFormData.age_start}
                onChange={e =>
                  setClassFormData({
                    ...classFormData,
                    age_start: parseInt(e.target.value, 10) || 5,
                  })
                }
                error={!!classFormErrors.age_start}
                helperText={classFormErrors.age_start}
                inputProps={{ min: 2, max: 99 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label="Age End"
                value={classFormData.age_end}
                onChange={e =>
                  setClassFormData({
                    ...classFormData,
                    age_end: parseInt(e.target.value, 10) || 18,
                  })
                }
                error={!!classFormErrors.age_end}
                helperText={classFormErrors.age_end}
                inputProps={{ min: 2, max: 99 }}
                required
              />
            </Stack>
            <TextField
              fullWidth
              type="number"
              label="Duration (minutes)"
              value={classFormData.duration}
              onChange={e =>
                setClassFormData({
                  ...classFormData,
                  duration: parseInt(e.target.value, 10) || 60,
                })
              }
              error={!!classFormErrors.duration}
              helperText={
                classFormErrors.duration || 'Must be a multiple of 15 minutes'
              }
              inputProps={{ min: 15, step: 15 }}
              required
            />
            <TextField
              fullWidth
              label="Notes"
              value={classFormData.notes}
              onChange={e =>
                setClassFormData({ ...classFormData, notes: e.target.value })
              }
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsQuickAddOpen(false);
              setClassFormData({
                class_name: '',
                styles: [],
                level: '',
                age_start: 5,
                age_end: 18,
                duration: 60,
                notes: '',
              });
              setClassFormErrors({});
            }}
            disabled={isCreatingClass}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              // Validate form
              const errors: typeof classFormErrors = {};
              if (!classFormData.class_name.trim()) {
                errors.class_name = 'Class name is required';
              }
              if (classFormData.styles.length === 0) {
                errors.styles = 'At least one style is required';
              }
              if (!classFormData.level.trim()) {
                errors.level = 'Level is required';
              }
              if (classFormData.age_start < 2 || classFormData.age_start > 99) {
                errors.age_start = 'Age start must be between 2 and 99';
              }
              if (
                classFormData.age_end < classFormData.age_start ||
                classFormData.age_end > 99
              ) {
                errors.age_end =
                  'Age end must be greater than age start and max 99';
              }
              if (classFormData.duration % 15 !== 0) {
                errors.duration = 'Duration must be a multiple of 15 minutes';
              }

              setClassFormErrors(errors);
              if (Object.keys(errors).length > 0) return;

              // Create class
              try {
                const newClass = await createClass({
                  scheduleId,
                  studioId,
                  data: {
                    ...classFormData,
                    duration: classFormData.duration / 60, // Convert to hours
                  },
                }).unwrap();

                // Close quick-add dialog
                setIsQuickAddOpen(false);
                setClassFormData({
                  class_name: '',
                  styles: [],
                  level: '',
                  age_start: 5,
                  age_end: 18,
                  duration: 60,
                  notes: '',
                });
                setClassFormErrors({});

                // Auto-select the newly created class
                setSelectedClassId(newClass.id);
                setDuration(newClass.duration);
              } catch (error) {
                console.error('Failed to create class:', error);
              }
            }}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreatingClass}
          >
            {isCreatingClass ? 'Creating...' : 'Create Class'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddClassDialog;
